import React, { useEffect, useRef } from 'react';
import { useOrganizationId } from '@features/user';
import { Alert, Box, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useGetSemanticContainerQuery } from '../store/api';

import { SemanticTileGrid } from './SemanticTileGrid';
import { TileSkeleton } from './TileSkeleton';

const GridSkeleton: React.FC = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
      gap: 2,
    }}
  >
    {[0, 1, 2, 3, 4, 5].map(i => (
      <TileSkeleton key={i} />
    ))}
  </Box>
);

/**
 * The practice-metrics dashboard (`/metrics`). Fetches the SDUI container
 * for the current org and renders the tiles through the real ComponentSlot
 * path. Backend gates the route to practice_administrator and scopes the org
 * fail-closed from auth.
 */
export const SemanticDashboardPage: React.FC = () => {
  const { practiceMetrics } = useEventTracking();
  const organizationId = useOrganizationId() ?? undefined;
  const { data, isLoading, error } = useGetSemanticContainerQuery(
    { organizationId, windowDays: 90 },
    { skip: !organizationId },
  );

  const components = data?.sections.flatMap(s => s.components) ?? [];

  // Open / dwell: fire once the page is genuinely viewed (organization + data in);
  // the cleanup reports time-on-dashboard as the attention-depth signal.
  const countRef = useRef(0);
  countRef.current = components.length;
  const viewing = !!organizationId && !!data;
  useEffect(() => {
    if (!viewing) return;
    const startedAt = Date.now();
    practiceMetrics.dashboardOpened({
      organization_id: organizationId,
      tile_count: countRef.current,
    });
    return () => {
      practiceMetrics.dashboardExited({
        organization_id: organizationId,
        duration_seconds: Math.round((Date.now() - startedAt) / 1000),
      });
    };
  }, [viewing, organizationId, practiceMetrics]);

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto', py: 4, px: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
        Practice metrics
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Trailing 90 days
      </Typography>

      {error ? (
        <Alert severity="error">Couldn&apos;t load your practice metrics. Please try again.</Alert>
      ) : isLoading || !data ? (
        <GridSkeleton />
      ) : components.length === 0 ? (
        <Alert severity="info">
          No metrics clear the ≥15 applicable-sample gate for this practice yet.
        </Alert>
      ) : (
        <SemanticTileGrid components={components} entityId={organizationId} surface="dashboard" />
      )}
    </Box>
  );
};

export default SemanticDashboardPage;
